import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Signal,
  signal,
  ViewChild,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AlertController, ModalController, Platform } from '@ionic/angular/standalone';
import { TranslocoService } from '@jsverse/transloco';
import { filter, from, of, startWith, Subject, switchMap, take, takeUntil } from 'rxjs';
import { DuelTierDataWithMeta } from 'src/app/core/services/tiers/tiers.service';
import { WaitingOpponentsData } from 'src/app/core/api/player/schemas/waiting-opponents';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';
import { EventName } from 'src/app/core/services/analytics/event-names';
import { OperatorService } from 'src/app/core/services/operator/operator.service';
import { TiersService } from 'src/app/core/services/tiers/tiers.service';
import { WaitingOpponentsService } from 'src/app/core/services/waiting-opponents/waiting-opponents.service';
import { GameId } from 'src/app/model/game/game-id';
import { ContinueGameDrawerComponent } from 'src/app/ui/drawers/continue-game-drawer/continue-game-drawer.component';
import { WagerDrawerComponent } from 'src/app/ui/drawers/wager-drawer/wager-drawer.component';
import { GameControlDisplay } from '../../model/game-control-display';
import {
  HintMessageFetcher,
  NotificationHintComponent,
} from 'src/app/ui/notification-hint/notification-hint.component';
import { ActivityFeedService } from 'src/app/core/services/activity-feed/activity-feed.service';
import { TournamentService } from 'src/app/core/services/tournament/tournament.service';
import { TournamentDataModel } from 'src/app/core/services/tournament/model/tournament-data-model';
import { ButtonComponent } from '../../../../../ui/buttons/button/button.component';
import { NotificationHintComponent as NotificationHintComponent_1 } from '../../../../../ui/notification-hint/notification-hint.component';
import { CountdownPillComponent } from '../../../../../ui/countdown-pill/countdown-pill.component';
import { createUnfinishedCountdown } from 'src/app/shared/utils/unfinished-countdown';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { getModalConfig, shouldShowAsSheetModal } from 'src/app/shared/utils/modal.utils';
import { GameTutorialService } from 'src/app/core/services/game-tutorial/game-tutorial.service';
import { RoomsResultsService } from 'src/app/core/services/rooms-results/rooms-results.service';
import { PaymentEventData } from 'src/app/shared/types/payment-events';
import { StorageService } from 'src/app/core/services/storage/storage.service';

@Component({
  selector: 'app-loading-game-controls',
  templateUrl: './loading-game-controls.component.html',
  styleUrls: ['./loading-game-controls.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CountdownPillComponent, NotificationHintComponent_1, ButtonComponent],
})
export class LoadingGameControlsComponent
  implements OnInit, OnDestroy, AfterViewInit, GameControlDisplay
{
  @ViewChild('hintMessage') set hintMessageRef(hintMessage: NotificationHintComponent | undefined) {
    if (hintMessage) {
      hintMessage.start();
    }
  }
  @Input({ required: true }) gameId!: GameId;
  @Input() autoOpenWagerDrawer = false;

  @Output() showPaymentEvent = new EventEmitter<PaymentEventData>();
  @Output() launchPracticeEvent = new EventEmitter<void>();
  @Output() launchLocalEvent = new EventEmitter<void>();

  private translocoService = inject(TranslocoService);
  private platform = inject(Platform);
  private modalController = inject(ModalController);
  private operatorService = inject(OperatorService);
  private tiersService = inject(TiersService);
  private waitingOpponentsService = inject(WaitingOpponentsService);
  private analyticsService = inject(AnalyticsService);
  private roomsResultsService = inject(RoomsResultsService);
  private activityFeedService = inject(ActivityFeedService);
  private tournamentService = inject(TournamentService);
  private themeService = inject(ThemeService);
  private gameTutorialService = inject(GameTutorialService);
  private storageService = inject(StorageService);
  private alertController = inject(AlertController);

  private destroy$ = new Subject<void>();
  private readonly allTiers = computed(() => this.tiersService.duelTiers() ?? []);
  private currentTournamentData = signal<TournamentDataModel | undefined>(undefined);

  private hasControls = signal(true);
  readonly showingControls: Signal<boolean> = this.hasControls.asReadonly();

  waitingOpponentsStandard = signal<WaitingOpponentsData | undefined>(undefined);

  readonly hasUnfinishedRoom = this.roomsResultsService.hasUnfinishedRoom;

  private initialised$ = toObservable(this.roomsResultsService.initialised);

  readonly unfinishedTimeRemaining = createUnfinishedCountdown(
    this.roomsResultsService,
    this.translocoService,
  );

  readonly playButtonLabel = computed(() =>
    this.hasUnfinishedRoom()
      ? this.getTranslation('continue-game')
      : this.getTranslation('play-now'),
  );

  async ngOnInit(): Promise<void> {
    await this.tournamentService.refreshCurrentTournament();

    this.tournamentService.currentTournamentData$
      .pipe(
        startWith(this.tournamentService.currentTournamentSnapshot),
        switchMap((currentTournamentData) => {
          this.currentTournamentData.set(currentTournamentData);
          if (!this.themeService.hasWaitingOpponents()) {
            return of(undefined);
          }
          return from(
            this.waitingOpponentsService.fetchWaitingOpponents(
              this.gameId,
              currentTournamentData?.tournamentId,
            ),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((waitingOpponents) => {
        this.waitingOpponentsStandard.set(waitingOpponents);
      });

    this.operatorService.updateBalance(OperatorService.IsDemo);

    this.checkUnfinishedRoom();

    if (this.autoOpenWagerDrawer) {
      await this.openWagerModal();
    }
  }

  ngAfterViewInit(): void {
    this.initialised$
      .pipe(
        filter((initialised) => initialised),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe(async () => {
        if (!this.roomsResultsService.hasUnfinishedRoom()) {
          return;
        }

        const asSheetModal = shouldShowAsSheetModal(this.platform, this.themeService);
        const continueGameModal = await this.modalController.create({
          component: ContinueGameDrawerComponent,
          ...getModalConfig(asSheetModal, { mobile: 'custom-modal-height' }),
        });
        await continueGameModal.present();

        this.analyticsService.log(EventName.ContinueGamePopupShow);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public fetchHints: HintMessageFetcher = (request) => {
    return this.activityFeedService.fetchHints(request.isInitial);
  };

  async playButtonClicked(): Promise<void> {
    if (this.hasUnfinishedRoom()) {
      const gameContinued = await this.roomsResultsService.launchUnfinishedGame();
      if (gameContinued) {
        this.analyticsService.log(EventName.ContinueGameScreenLaunchGame);
        return;
      }
    }
    await this.openWagerModal();
  }

  async openWagerModal(): Promise<void> {
    this.analyticsService.log(EventName.ChangePrizeButtonClicked);

    const asSheetModal = shouldShowAsSheetModal(this.platform, this.themeService);

    const wagerModal = await this.modalController.create({
      component: WagerDrawerComponent,
      componentProps: {
        gameId: this.gameId,
        allTiers: this.allTiers,
        currentTournamentData: this.currentTournamentData,
        waitingOpponentsStandard: this.waitingOpponentsStandard,
      },
      ...getModalConfig(asSheetModal, { mobile: 'custom-modal-height' }),
    });

    await wagerModal.present();

    await Promise.all([
      this.tournamentService.refreshCurrentTournament(),
      this.roomsResultsService.refreshResults(),
    ]);

    const { data, role } = await wagerModal.onDidDismiss();
    if (role === 'practiceClick') {
      this.analyticsService.log(EventName.PracticeButtonClicked);
      this.launchPracticeEvent.emit();
    } else if (role === 'localClick') {
      this.launchLocalEvent.emit();
    } else if (role === 'wagerClick') {
      this.analyticsService.log(EventName.PlayButtonClicked, { tierId: data.tierId });
      this.showPaymentEvent.emit(data);
    } else if (role === 'tournamentPlayClick') {
      this.analyticsService.log(EventName.TournamentPlayButtonClicked);
      this.showPaymentEvent.emit(data);
    }
  }

  private async checkUnfinishedRoom(): Promise<void> {
    const modalPresent = await this.modalController.getTop();

    if (!modalPresent) {
      this.initialised$
        .pipe(
          filter((initialised) => initialised),
          take(1),
          takeUntil(this.destroy$),
        )
        .subscribe(async () => {
          try {
            // Before showing the tutorial, make sure there isn't an unfinished room
            if (!this.roomsResultsService.hasUnfinishedRoom()) {
              await this.gameTutorialService.tryShowTutorial(this.gameId);
            }
          } catch (error) {
            this.analyticsService.handleError(error, 'checkUnfinishedRoom');
          }
        });
    }
  }

  private selectedTierData(index: number): DuelTierDataWithMeta | undefined {
    if (this.allTiers().length > 0 && index >= 0 && index < this.allTiers().length) {
      return this.allTiers()[index];
    }
    return undefined;
  }

  private getTranslation(key: string, params?: Record<string, unknown>): string {
    return this.translocoService.translate(`components.loading-game-controls.${key}`, params);
  }
}
